import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2, Building2, Navigation, Plane, Utensils, History } from "lucide-react";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
// Pays autorisés — on garde une whitelist (France + voisins directs
// francophones/européens fréquents pour SAOR : Monaco, Belgique, Suisse,
// Italie, Espagne, Luxembourg, Allemagne, UK). Retirer complètement
// `country=` rendrait les résultats trop bruyants (adresses homonymes
// dans des pays très lointains). Cette liste couvre >99 % des courses
// SAOR probables et laisse le client taper NIMPORTE quelle ville de ces
// pays — plus de blocage au niveau bbox comme avant.
const COUNTRIES = "FR,MC,BE,CH,IT,ES,LU,DE,GB,PT,NL";

// ── Region centres / envelopes ─────────────────────────────────────────
// PACA / Côte d'Azur — Saor's historical market.
const NICE_COTE_DAZUR = "7.2620,43.7102";
const COTE_DAZUR_BBOX = [6.10, 43.10, 7.75, 44.20];
// Île-de-France — Paris + petite/grande couronne.
const PARIS_CENTER = "2.3522,48.8566";
const IDF_BBOX = [1.45, 48.10, 3.55, 49.25];
// Auvergne-Rhône-Alpes → Lyon
const LYON_BBOX = [4.55, 45.55, 5.15, 45.95];
// Provence → Marseille
const MARSEILLE_BBOX = [5.20, 43.15, 5.65, 43.45];
// Aquitaine → Bordeaux
const BORDEAUX_BBOX = [-0.85, 44.75, -0.45, 45.05];
// Occitanie → Toulouse
const TOULOUSE_BBOX = [1.30, 43.45, 1.65, 43.75];

const REGION_CONFIG = {
  paca: { center: NICE_COTE_DAZUR },
  idf: { center: PARIS_CENTER },
};

/**
 * Détecte automatiquement la région du client à partir d'une paire
 * "lng,lat" (celle passée dans le prop `proximityCoord` OU celle obtenue
 * via `navigator.geolocation`). Retourne la clé du preset à afficher :
 *   • "paca"       → suggestions Côte d'Azur
 *   • "idf"        → suggestions Paris / IDF
 *   • "lyon" / "marseille" / "bordeaux" / "toulouse" → suggestions
 *     ciblées grandes villes françaises
 *   • null         → hors zone connue, on n'affiche AUCUN preset (juste
 *     "Récemment utilisées" et les résultats live Mapbox)
 */
function detectRegionFromProximity(proximityStr) {
  if (!proximityStr) return null;
  const parts = String(proximityStr).split(",").map(Number);
  if (parts.length !== 2 || !parts.every(Number.isFinite)) return null;
  const [lng, lat] = parts;
  const inBbox = (bbox) => lng >= bbox[0] && lng <= bbox[2] && lat >= bbox[1] && lat <= bbox[3];
  if (inBbox(IDF_BBOX)) return "idf";
  if (inBbox(COTE_DAZUR_BBOX)) return "paca";
  if (inBbox(LYON_BBOX)) return "lyon";
  if (inBbox(MARSEILLE_BBOX)) return "marseille";
  if (inBbox(BORDEAUX_BBOX)) return "bordeaux";
  if (inBbox(TOULOUSE_BBOX)) return "toulouse";
  return null;
}

// Quick-pick destinations shown when the input gains focus (à la Waze).
// Used as static suggestions ABOVE the live Mapbox results when the input is
// empty so dispatchers / hotel staff can one-tap the most common destinations.
const COTE_DAZUR_PRESETS = [
  {
    id: "preset-nce",
    name: "Aéroport Nice Côte d'Azur (T2)",
    address: "Terminal 2 — 06200 Nice",
    coord: { lng: 7.2167, lat: 43.6584 },
    icon: "airport",
  },
  {
    id: "preset-nce-t1",
    name: "Aéroport Nice Côte d'Azur (T1)",
    address: "Terminal 1 — 06200 Nice",
    coord: { lng: 7.2148, lat: 43.6630 },
    icon: "airport",
  },
  {
    id: "preset-gare-nice",
    name: "Gare SNCF Nice-Ville",
    address: "Avenue Thiers — 06000 Nice",
    coord: { lng: 7.2613, lat: 43.7045 },
    icon: "train",
  },
  {
    id: "preset-promenade",
    name: "Promenade des Anglais",
    address: "06000 Nice",
    coord: { lng: 7.2620, lat: 43.6951 },
    icon: "place",
  },
  {
    id: "preset-vieux-nice",
    name: "Vieux-Nice (Place Massena)",
    address: "06000 Nice",
    coord: { lng: 7.2715, lat: 43.6975 },
    icon: "place",
  },
  {
    id: "preset-monaco",
    name: "Monaco — Place du Casino",
    address: "98000 Monaco",
    coord: { lng: 7.4278, lat: 43.7397 },
    icon: "place",
  },
  {
    id: "preset-cannes",
    name: "Cannes — Palais des Festivals",
    address: "1 Bd de la Croisette — 06400 Cannes",
    coord: { lng: 7.0173, lat: 43.5511 },
    icon: "place",
  },
  {
    id: "preset-antibes",
    name: "Antibes — Vieux Port",
    address: "06600 Antibes",
    coord: { lng: 7.1283, lat: 43.5810 },
    icon: "place",
  },
  {
    id: "preset-villefranche",
    name: "Villefranche-sur-Mer — Port",
    address: "06230 Villefranche-sur-Mer",
    coord: { lng: 7.3107, lat: 43.7045 },
    icon: "place",
  },
  {
    id: "preset-menton",
    name: "Menton — Vieille Ville",
    address: "06500 Menton",
    coord: { lng: 7.5031, lat: 43.7757 },
    icon: "place",
  },
  {
    id: "preset-eze",
    name: "Èze — Village",
    address: "06360 Èze",
    coord: { lng: 7.3624, lat: 43.7280 },
    icon: "place",
  },
];

// Île-de-France presets — equivalent quick-picks for Paris drivers.
const IDF_PRESETS = [
  { id: "preset-cdg", name: "Aéroport Paris-Charles-de-Gaulle", address: "95700 Roissy-en-France", coord: { lng: 2.5479, lat: 49.0097 }, icon: "airport" },
  { id: "preset-orly", name: "Aéroport Paris-Orly", address: "94390 Orly", coord: { lng: 2.3650, lat: 48.7263 }, icon: "airport" },
  { id: "preset-lebourget", name: "Aéroport du Bourget", address: "93350 Le Bourget", coord: { lng: 2.4413, lat: 48.9694 }, icon: "airport" },
  { id: "preset-gare-nord", name: "Gare du Nord", address: "75010 Paris", coord: { lng: 2.3553, lat: 48.8809 }, icon: "train" },
  { id: "preset-gare-lyon", name: "Gare de Lyon", address: "75012 Paris", coord: { lng: 2.3737, lat: 48.8443 }, icon: "train" },
  { id: "preset-saint-lazare", name: "Gare Saint-Lazare", address: "75008 Paris", coord: { lng: 2.3252, lat: 48.8758 }, icon: "train" },
  { id: "preset-montparnasse", name: "Gare Montparnasse", address: "75015 Paris", coord: { lng: 2.3210, lat: 48.8410 }, icon: "train" },
  { id: "preset-louvre", name: "Musée du Louvre", address: "75001 Paris", coord: { lng: 2.3376, lat: 48.8606 }, icon: "place" },
  { id: "preset-eiffel", name: "Tour Eiffel", address: "Champ de Mars — 75007 Paris", coord: { lng: 2.2945, lat: 48.8584 }, icon: "place" },
  { id: "preset-defense", name: "La Défense", address: "92800 Puteaux", coord: { lng: 2.2375, lat: 48.8918 }, icon: "place" },
  { id: "preset-versailles", name: "Château de Versailles", address: "78000 Versailles", coord: { lng: 2.1204, lat: 48.8049 }, icon: "place" },
  { id: "preset-disney", name: "Disneyland Paris", address: "77777 Marne-la-Vallée", coord: { lng: 2.7833, lat: 48.8722 }, icon: "place" },
];

// Lyon / Auvergne-Rhône-Alpes
const LYON_PRESETS = [
  { id: "preset-lys", name: "Aéroport Lyon-Saint-Exupéry", address: "69125 Colombier-Saugnieu", coord: { lng: 5.0908, lat: 45.7256 }, icon: "airport" },
  { id: "preset-gare-part-dieu", name: "Gare Lyon Part-Dieu", address: "69003 Lyon", coord: { lng: 4.8593, lat: 45.7605 }, icon: "train" },
  { id: "preset-gare-perrache", name: "Gare Lyon Perrache", address: "69002 Lyon", coord: { lng: 4.8265, lat: 45.7484 }, icon: "train" },
  { id: "preset-bellecour", name: "Place Bellecour", address: "69002 Lyon", coord: { lng: 4.8321, lat: 45.7578 }, icon: "place" },
  { id: "preset-vieux-lyon", name: "Vieux Lyon", address: "69005 Lyon", coord: { lng: 4.8271, lat: 45.7627 }, icon: "place" },
];

// Marseille / Provence
const MARSEILLE_PRESETS = [
  { id: "preset-mrs", name: "Aéroport Marseille-Provence", address: "13727 Marignane", coord: { lng: 5.2154, lat: 43.4393 }, icon: "airport" },
  { id: "preset-gare-saint-charles", name: "Gare Marseille Saint-Charles", address: "13001 Marseille", coord: { lng: 5.3800, lat: 43.3025 }, icon: "train" },
  { id: "preset-vieux-port", name: "Vieux-Port", address: "13001 Marseille", coord: { lng: 5.3699, lat: 43.2951 }, icon: "place" },
  { id: "preset-notre-dame-garde", name: "Notre-Dame-de-la-Garde", address: "13006 Marseille", coord: { lng: 5.3714, lat: 43.2839 }, icon: "place" },
  { id: "preset-mucem", name: "MuCEM", address: "13002 Marseille", coord: { lng: 5.3611, lat: 43.2967 }, icon: "place" },
];

// Bordeaux / Aquitaine
const BORDEAUX_PRESETS = [
  { id: "preset-bod", name: "Aéroport Bordeaux-Mérignac", address: "33700 Mérignac", coord: { lng: -0.7156, lat: 44.8286 }, icon: "airport" },
  { id: "preset-gare-saint-jean", name: "Gare Bordeaux Saint-Jean", address: "33800 Bordeaux", coord: { lng: -0.5566, lat: 44.8258 }, icon: "train" },
  { id: "preset-place-bourse", name: "Place de la Bourse", address: "33000 Bordeaux", coord: { lng: -0.5697, lat: 44.8412 }, icon: "place" },
  { id: "preset-cite-du-vin", name: "Cité du Vin", address: "33300 Bordeaux", coord: { lng: -0.5504, lat: 44.8635 }, icon: "place" },
];

// Toulouse / Occitanie
const TOULOUSE_PRESETS = [
  { id: "preset-tls", name: "Aéroport Toulouse-Blagnac", address: "31700 Blagnac", coord: { lng: 1.3639, lat: 43.6294 }, icon: "airport" },
  { id: "preset-gare-matabiau", name: "Gare Toulouse Matabiau", address: "31500 Toulouse", coord: { lng: 1.4530, lat: 43.6117 }, icon: "train" },
  { id: "preset-capitole", name: "Place du Capitole", address: "31000 Toulouse", coord: { lng: 1.4437, lat: 43.6045 }, icon: "place" },
];

const PRESETS_BY_REGION = {
  paca: COTE_DAZUR_PRESETS,
  idf: IDF_PRESETS,
  lyon: LYON_PRESETS,
  marseille: MARSEILLE_PRESETS,
  bordeaux: BORDEAUX_PRESETS,
  toulouse: TOULOUSE_PRESETS,
};

const REGION_LABELS = {
  paca: "Suggestions Côte d'Azur",
  idf: "Suggestions Paris — Île-de-France",
  lyon: "Suggestions Lyon",
  marseille: "Suggestions Marseille",
  bordeaux: "Suggestions Bordeaux",
  toulouse: "Suggestions Toulouse",
};
// LocalStorage key for the per-user address history (Waze-like quick access).
const RECENTS_KEY = "saor:address-recents";
const RECENTS_MAX = 5;

function loadRecents() {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch (_) { return []; }
}

function saveRecent(entry) {
  try {
    const list = loadRecents().filter((e) => e.text !== entry.text);
    list.unshift({ ...entry, at: Date.now() });
    localStorage.setItem(RECENTS_KEY, JSON.stringify(list.slice(0, RECENTS_MAX)));
  } catch (_) {}
}

/**
 * Waze/Google-Maps-like address autocomplete powered by Mapbox **Search Box
 * API v1** (the modern one — v5/places is legacy and misses famous POIs).
 *
 *   1. POST `/search/searchbox/v1/suggest` as the user types → returns a
 *      mix of POIs, addresses, streets, places. Each item has a `mapbox_id`.
 *   2. When the user picks one, call `/retrieve/{mapbox_id}` to resolve the
 *      full address + coords (required by Search Box API semantics).
 *
 * Uses a persistent `session_token` (UUID v4) per component mount to keep
 * billing as one session — as recommended by Mapbox.
 */

// Create a stable session token across React re-renders but per component instance.
function genSessionToken() {
  try {
    return crypto.randomUUID();
  } catch (_) {
    // RFC 4122 v4 fallback for older browsers
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}

export default function AddressAutocomplete({
  value = "",
  onChange,
  onSelect, // (text, { lng, lat }) => void  — optional callback with precise coords
  placeholder = "Adresse ou lieu (ex : Aéroport, Gare, Hôtel…)",
  required = false,
  proximityCoord = null, // {lng, lat} — biais primaire (position hôtel / client)
  // Region prop : "auto" (défaut client, détecte via GPS), "paca", "idf",
  // "lyon", "marseille", "bordeaux", "toulouse". Les pages hôtel/dispatch
  // gardent leur région fixe pour conserver leur workflow habituel.
  region = "auto",
  // When true, suggestions render INLINE under the input (no React portal).
  // Use this inside Radix Dialogs / forms with enough vertical room — the
  // portal mode is only needed for tight overflow-hidden containers like
  // the client bottom sheet.
  inline = true,
  ...rest
}) {
  // Résolution région : soit forcée par la prop, soit auto-détectée via
  // la position (proximityCoord d'abord, puis GPS navigateur si dispo).
  // On garde "paca" comme fallback historique pour les pages hôtel qui
  // s'attendent à ce comportement — mais côté client on passe "auto".
  const regionCfg = REGION_CONFIG[region] || REGION_CONFIG.paca;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [activeIdx, setActiveIdx] = useState(-1);
  // Initial proximity priority:
  //  1) explicit `proximityCoord` prop (hotel's own address — local-first)
  //  2) NICE_COTE_DAZUR default (Saor's primary market)
  // Later overwritten by the user's GPS position if the browser grants it.
  const [proximity, setProximity] = useState(
    proximityCoord && proximityCoord.lng != null && proximityCoord.lat != null
      ? `${Number(proximityCoord.lng).toFixed(5)},${Number(proximityCoord.lat).toFixed(5)}`
      : regionCfg.center
  );
  // Région effective utilisée pour les presets. Recalculée dès que
  // `proximity` change — permet à un client parisien d'obtenir les
  // presets IDF, un client marseillais → Marseille, etc.
  const activeRegion = region === "auto"
    ? detectRegionFromProximity(proximity)
    : region;
  const PRESETS = activeRegion ? (PRESETS_BY_REGION[activeRegion] || []) : [];
  const PRESET_LABEL = activeRegion ? REGION_LABELS[activeRegion] : null;
  const [recents, setRecents] = useState(() => loadRecents());
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const debounce = useRef(null);
  const skipNext = useRef(false);
  const reqId = useRef(0);
  const sessionToken = useRef(genSessionToken());

  // Live position for the portaled dropdown — recomputed when the input
  // gains/loses focus, on scroll, and on viewport resize. This bypasses
  // any `overflow-hidden` ancestor (e.g. the client bottom sheet).
  const [popRect, setPopRect] = useState(null);
  useEffect(() => {
    if (!open) { setPopRect(null); return; }
    const compute = () => {
      // Anchor on the wrapping <div> rather than the raw <input>: in the
      // client bottom sheet the input sits inside a card with padding, so
      // the wrapper's rect matches the visual edge users expect.
      const r = (wrapRef.current || inputRef.current)?.getBoundingClientRect?.();
      if (!r) return;
      // Round to whole pixels — sub-pixel rect values fluctuate between
      // re-renders (suggestions arriving change the layout reflow) and that
      // is exactly what makes the popover « bouger » when the user types.
      const next = {
        top: Math.round(r.bottom + 4),
        left: Math.round(r.left),
        width: Math.round(r.width),
      };
      setPopRect((prev) => {
        if (prev && prev.top === next.top && prev.left === next.left && prev.width === next.width) {
          return prev; // identical → bail out, prevents re-renders & jitter
        }
        return next;
      });
    };
    compute();
    // Watch the wrapper for layout/size changes (suggestions arriving,
    // sheet content shifting, virtual keyboard pushing the form, etc.)
    let ro = null;
    if (typeof ResizeObserver !== "undefined" && wrapRef.current) {
      ro = new ResizeObserver(compute);
      ro.observe(wrapRef.current);
    }
    window.addEventListener("scroll", compute, true);
    window.addEventListener("resize", compute);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("scroll", compute, true);
      window.removeEventListener("resize", compute);
    };
  }, [open]);

  // Track `proximityCoord` updates (e.g. hotel profile loads async).
  useEffect(() => {
    if (proximityCoord && proximityCoord.lng != null && proximityCoord.lat != null) {
      setProximity(`${Number(proximityCoord.lng).toFixed(5)},${Number(proximityCoord.lat).toFixed(5)}`);
    }
  }, [proximityCoord?.lng, proximityCoord?.lat]);

  // Best-effort GPS proximity — takes priority over the hotel address when
  // the browser grants permission (the user might be doing a booking from a
  // different location than the hotel itself).
  useEffect(() => {
    if (!("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setProximity(`${pos.coords.longitude.toFixed(5)},${pos.coords.latitude.toFixed(5)}`),
      () => {},
      { maximumAge: 300_000, timeout: 4000, enableHighAccuracy: false },
    );
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const fetchSuggestions = async (q) => {
    if (!q || q.trim().length < 2 || !MAPBOX_TOKEN) { setSuggestions([]); return; }
    const myReq = ++reqId.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        q,
        access_token: MAPBOX_TOKEN,
        session_token: sessionToken.current,
        language: "fr",
        country: COUNTRIES,
        limit: "8",
        proximity,
        // PAS de bbox — le client doit pouvoir chercher toute la France
        // + voisins (Monaco, Belgique, Suisse, Italie…). `proximity`
        // suffit à trier les résultats les plus proches en premier.
      });
      const r = await fetch(`https://api.mapbox.com/search/searchbox/v1/suggest?${params.toString()}`);
      const d = await r.json();
      if (myReq !== reqId.current) return;
      const items = (d.suggestions || [])
        // Skip "category" / "brand" rows (Mapbox suggests these as facets when
        // the user types a generic word like "hotel" or "Monoprix" — they have
        // no coords and clutter the dropdown).
        .filter((s) => !["category", "brand"].includes(s.feature_type))
        .slice(0, 6)
        .map((s) => ({
          id: s.mapbox_id,
          name: s.name,
          feature_type: s.feature_type,
          maki: s.maki,
          poi_category: (s.poi_category || []).join(", "),
          address: s.full_address || s.place_formatted || s.address || "",
          distance_m: s.distance,
        }));
      setSuggestions(items);
      setActiveIdx(-1);
    } catch {
      if (myReq === reqId.current) setSuggestions([]);
    } finally {
      if (myReq === reqId.current) setLoading(false);
    }
  };

  const handleChange = (e) => {
    const v = e.target.value;
    onChange?.(v);
    setOpen(true);
    if (skipNext.current) { skipNext.current = false; return; }
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => fetchSuggestions(v), 150);
  };

  // Join name + address into a single display string while avoiding the
  // duplicate-prefix bug: Mapbox often returns `full_address` already
  // starting with the venue/street name (e.g. name="41 Rue Lamartine",
  // address="41 Rue Lamartine, 06000 Nice, France"). Concatenating "name · address"
  // would produce "41 Rue Lamartine · 41 Rue Lamartine, 06000 Nice, France".
  const formatNameAddress = (name, address) => {
    const n = (name || "").trim();
    const a = (address || "").trim();
    if (!n) return a;
    if (!a) return n;
    if (a.toLowerCase().startsWith(n.toLowerCase())) return a;
    return `${n} · ${a}`;
  };

  const pick = async (s) => {
    skipNext.current = true;
    // Fallback name in case the retrieve call fails (network, quota, etc.)
    let final = formatNameAddress(s.name, s.address);
    let coord = null;
    onChange?.(final);
    setSuggestions([]);
    setOpen(false);
    inputRef.current?.blur();
    // Ask Mapbox for the canonical address (Search Box API requires this
    // retrieve call per session — billing + precision).
    try {
      const params = new URLSearchParams({
        access_token: MAPBOX_TOKEN,
        session_token: sessionToken.current,
        language: "fr",
      });
      const r = await fetch(`https://api.mapbox.com/search/searchbox/v1/retrieve/${encodeURIComponent(s.id)}?${params.toString()}`);
      if (r.ok) {
        const d = await r.json();
        const feat = (d.features || [])[0];
        if (feat) {
          const props = feat.properties || {};
          const pretty = props.full_address || props.place_formatted || props.address;
          if (pretty) {
            final = props.name ? `${props.name} · ${pretty}` : pretty;
            onChange?.(final);
          }
          // Extract precise coordinates so the backend doesn't have to
          // re-geocode (avoids the v5/places imprecision on famous POIs).
          const c = feat.geometry?.coordinates;
          if (Array.isArray(c) && c.length >= 2) {
            coord = { lng: Number(c[0]), lat: Number(c[1]) };
          }
        }
      }
    } catch { /* keep the fallback */ }
    if (coord) onSelect?.(final, coord);
    // Persist in the per-browser address history (Waze-like quick recall)
    saveRecent({
      text: final,
      name: s.name,
      address: s.address,
      feature_type: s.feature_type,
      poi_category: s.poi_category,
      coord,
    });
    setRecents(loadRecents());
    // New session after a successful pick (Mapbox best practice)
    sessionToken.current = genSessionToken();
  };

  const pickRecent = (r) => {
    skipNext.current = true;
    onChange?.(r.text);
    if (r.coord && r.coord.lng != null && r.coord.lat != null) {
      onSelect?.(r.text, r.coord);
    }
    setSuggestions([]);
    setOpen(false);
    inputRef.current?.blur();
    // Bump to top
    saveRecent({ text: r.text, name: r.name, address: r.address, feature_type: r.feature_type, poi_category: r.poi_category, coord: r.coord });
    setRecents(loadRecents());
  };

  // One-tap presets — common Côte d'Azur destinations exposed when the input
  // is empty. They behave exactly like a Mapbox suggestion (no extra
  // /retrieve call needed since the coordinates are hard-coded).
  const pickPreset = (p) => {
    skipNext.current = true;
    const final = formatNameAddress(p.name, p.address);
    onChange?.(final);
    onSelect?.(final, p.coord);
    setSuggestions([]);
    setOpen(false);
    inputRef.current?.blur();
    saveRecent({
      text: final,
      name: p.name,
      address: p.address,
      feature_type: "preset",
      poi_category: "preset",
      coord: p.coord,
    });
    setRecents(loadRecents());
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && activeIdx >= 0) { e.preventDefault(); pick(suggestions[activeIdx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const showList = open && (loading || suggestions.length > 0 || (suggestions.length === 0 && (recents.length > 0 || !value)));
  const showRecents = open && suggestions.length === 0 && !loading && recents.length > 0;
  const showPresets = open && suggestions.length === 0 && !loading && !value;

  /** Shared content rendered both in the portal (default) and inline (when
   * `inline=true`). Extracted as a function so we don't duplicate JSX. */
  const renderListContent = () => (
    <>
      {loading && suggestions.length === 0 && (
        <div className="flex items-center gap-2 px-3 py-2.5 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Recherche en cours…
        </div>
      )}
      {showRecents && (
        <>
          <div className="flex items-center gap-1.5 bg-secondary/40 px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">
            <History className="h-3 w-3" /> Récemment utilisées
          </div>
          {recents.map((r, i) => (
            <button
              type="button"
              key={`r-${i}`}
              onMouseDown={(e) => e.preventDefault()} onClick={() => pickRecent(r)}
              onMouseEnter={() => setActiveIdx(-1)}
              className="flex w-full items-start gap-3 px-3 py-2.5 text-left text-sm transition-colors hover:bg-secondary"
              data-testid={`address-recent-${i}`}
            >
              <CategoryIcon feature_type={r.feature_type} poi_category={r.poi_category} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{r.name || r.text}</p>
                {r.address && <p className="truncate text-xs text-muted-foreground">{r.address}</p>}
              </div>
            </button>
          ))}
        </>
      )}
      {showPresets && PRESETS.length > 0 && (
        <>
          <div className="flex items-center gap-1.5 bg-secondary/40 px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">
            <MapPin className="h-3 w-3" /> {PRESET_LABEL || "Suggestions"}
          </div>
          {PRESETS.map((p) => (
            <button
              type="button"
              key={p.id}
              onMouseDown={(e) => e.preventDefault()} onClick={() => pickPreset(p)}
              onMouseEnter={() => setActiveIdx(-1)}
              className="flex w-full items-start gap-3 px-3 py-2.5 text-left text-sm transition-colors hover:bg-secondary"
              data-testid={`address-preset-${p.id}`}
            >
              {p.icon === "airport" ? (
                <Plane className="mt-0.5 h-4 w-4 flex-none text-primary" />
              ) : p.icon === "train" ? (
                <Navigation className="mt-0.5 h-4 w-4 flex-none text-primary" />
              ) : (
                <MapPin className="mt-0.5 h-4 w-4 flex-none text-primary" />
              )}
              <div className="min-w-0 flex-1">
                <p className="line-clamp-2 font-medium leading-tight">{p.name}</p>
                {p.address && (
                  <p className="mt-0.5 text-xs leading-snug text-muted-foreground">{p.address}</p>
                )}
              </div>
            </button>
          ))}
        </>
      )}
      {suggestions.map((s, i) => (
        <button
          type="button"
          key={s.id}
          onMouseDown={(e) => e.preventDefault()} onClick={() => pick(s)}
          onMouseEnter={() => setActiveIdx(i)}
          className={`flex w-full items-start gap-3 px-3 py-2.5 text-left text-sm transition-colors ${activeIdx === i ? "bg-primary/10" : "hover:bg-secondary"}`}
          data-testid={`address-suggestion-${i}`}
        >
          <CategoryIcon feature_type={s.feature_type} poi_category={s.poi_category} />
          <div className="min-w-0 flex-1">
            {/* Nom principal : max 2 lignes puis ellipsis. Address :
                affichée en entier avec wrap naturel (plus de truncate)
                pour que le client voie bien la ville et le code postal
                — feedback client Amine. */}
            <p className="line-clamp-2 font-medium leading-tight">{s.name}</p>
            {s.address && (
              <p className="mt-0.5 text-xs leading-snug text-muted-foreground">{s.address}</p>
            )}
          </div>
          {s.distance_m != null && (
            <span className="ml-2 flex-none self-center text-[10px] text-muted-foreground tabular-nums">
              {s.distance_m > 1000 ? `${(s.distance_m / 1000).toFixed(1)} km` : `${Math.round(s.distance_m)} m`}
            </span>
          )}
        </button>
      ))}
    </>
  );

  // Extract className from rest so we can prepend `w-full` without
  // it being overridden by a className passed via ...rest.
  const { className: restClassName, ...restProps } = rest;

  return (
    <div className="relative w-full" ref={wrapRef}>
      <Input
        ref={inputRef}
        value={value}
        onChange={handleChange}
        onFocus={() => {
          if (suggestions.length) setOpen(true);
          else if (value && value.length >= 2) fetchSuggestions(value);
          else if (recents.length) setOpen(true);
          else setOpen(true); // empty input → show Côte d'Azur presets
        }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        // `w-full` explicite pour que l'input remplisse tout l'espace
        // disponible du parent flex-1. Sans ça, les longues adresses
        // (ex. "Aéroport Nice Côte d'Azur (T1) - Terminal") sont
        // tronquées à ~30 caractères (feedback client Amine).
        className={`w-full ${restClassName || ""}`.trim()}
        {...restProps}
      />
      {showList && (inline ? (
        <div
          className="relative z-[60] mt-1 overflow-hidden rounded-xl border border-border bg-popover shadow-xl max-h-[260px] overflow-y-auto"
          data-testid="address-suggestions"
        >
          {renderListContent()}
        </div>
      ) : (popRect && createPortal(
        <div
          // Hauteur RÉDUITE à 260 px (au lieu de 380) pour ne PAS couvrir
          // le champ destination + les vehicle cards en dessous quand
          // l'utilisateur tape dans le champ départ. Feedback client
          // Amine : "on ne voit même pas la destination".
          className="z-[60] overflow-hidden rounded-xl border border-border bg-popover shadow-xl max-h-[260px] overflow-y-auto"
          style={{
            position: "fixed",
            top: popRect.top,
            left: popRect.left,
            width: popRect.width,
          }}
          onPointerDownCapture={(e) => e.stopPropagation()}
          onMouseDownCapture={(e) => e.stopPropagation()}
          data-testid="address-suggestions"
        >
          {renderListContent()}
        </div>,
        document.body,
      )))}
    </div>
  );
}


function CategoryIcon({ feature_type, poi_category }) {
  const base = "mt-0.5 h-4 w-4 flex-none text-primary";
  const cat = (poi_category || "").toLowerCase();
  if (/airport|aéroport|gare|train|transit|metro|station/.test(cat)) return <Plane className={base} />;
  if (/hotel|lodging|hébergement|resort/.test(cat)) return <Building2 className={base} />;
  if (/restaurant|bar|café|food/.test(cat)) return <Utensils className={base} />;
  if (feature_type === "street" || feature_type === "address") return <Navigation className={base} />;
  return <MapPin className={base} />;
}
